export type GA4Item = {

  /**
   * @description The id of the item.
   * @example HB-124-01
   */
  item_id: string

  /**
   * @description The name of the item.
   * @example Westin Heavenly Bed
   */
  item_name: string

  /**
   * @description The affiliation of the item.
   * @example Westin Hotels and Resorts
   */
  affiliation: string

  /**
   * @description The price of the item with discount included.
   * @example 2546.25
   */
  price: number

  /**
   * @description The discount applied to the price of the item.
   * @example 848.75
   */
  discount: number

  /**
   * @description The extensions of the items, separated by `;`.
   * @example Cal King;Standard 8.75\"
   */
  item_variant?: string

  /**
   * @description If it's a subscription product or not.
   * @example no
   */
  subscription: 'yes' | 'no'

  /**
   * @description The brand of the item.
   * @example Westin Store
   */
  item_brand: string

  /**
   * @description The category of the item.
   * @example The Heavenly® Bed
   */
  item_category: string

  item_category2?: string
  item_category3?: string
  item_category4?: string

  /**
   * @description The quantity of the item.
   * @example 1
   */
  quantity: number

  /**
   * @description The index of the item.
   * @example 0
   */
  index: number

  /**
   * @description The id of the list where the item is.
   * @example TheHeavenlyBed
   */
  item_list_id: string

  /**
   * @description The name of the list where the item is.
   * @example The Heavenly® Bed
   */
  item_list_name: string
}

export type GA4MainItem = {
  id: string
  name: string
  category: string
  value: number
}

export default class GA4Utils {


  /**
   * https://developers.google.com/analytics/devguides/collection/ga4/reference/events?sjid=18176790832793608239-NA&client_type=gtag#add_to_cart
   * @description Tracks the "add_to_cart" event pushing the data to the dataLayer object.  
   * When a set item is added to the cart, the `mainItem` is the set item and the `items` are the items in the set.
   */
  static trackAddToCartEvent = (items: GA4Item[], mainItem: GA4MainItem) => {
      const dataLayer = (window as any).dataLayer

      const value = items.reduce((acc, item) => acc + item.price * item.quantity, 0)

      dataLayer.push({ ecommerce: null })
      dataLayer.push({
          event: 'add_to_cart',
          ecommerce: {
              currency: 'USD',
              value: value,
              items: items
          },
          event_value: value,
          event_items: items.map((item) => ({
              id: item.item_id,
              google_business_vertical: 'retail'
          })),
          mainItem: mainItem
      })
  }

  /**
   * https://developers.google.com/analytics/devguides/collection/ga4/reference/events?sjid=18176790832793608239-NA&client_type=gtag#remove_from_cart
   * @description Tracks the "remove_from_cart" event pushing the data to the dataLayer object.
   */
  static trackRemoveFromCartEvent = (items: GA4Item[]) => {
      const dataLayer = (window as any).dataLayer

      const value = items.reduce((acc, item) => acc + item.price * item.quantity, 0)

      dataLayer.push({ ecommerce: null })
      dataLayer.push({
          event: 'remove_from_cart',
          ecommerce: {
              currency: 'USD',
              value: value,
              items: items
          }
      })
  }

  /**
   * https://developers.google.com/analytics/devguides/collection/ga4/reference/events?sjid=18176790832793608239-NA&client_type=gtag#view_cart
   */
  static trackViewCartEvent = (items: GA4Item[]) => {
      const dataLayer = (window as any).dataLayer

      const value = items.reduce((acc, item) => acc + item.price * item.quantity, 0)

      dataLayer.push({ ecommerce: null });
      dataLayer.push({
          event: "view_cart",
          ecommerce: {
              currency: 'USD',
              value: value,
              items: items
          }
      });
  }
}